import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getMeuPlano } from "../api/planosApi";
import { useAuth } from "../hooks/useAuth";

const PlanContext = createContext(null);

const PLANO_PADRAO = {
  id: null,
  nome: "Basico",
  nivel: 0,
  status: "ativo",
  expira_em: null,
  recursos: {},
};

/**
 * Plano do usuario logado + quais recursos ele libera.
 *
 * Quem manda de verdade e o middleware requireFeature no servidor; aqui e so
 * experiencia (menu com cadeado, rota que leva para a tela de planos). Por
 * isso, se /me/plano falhar, o contexto libera tudo em vez de travar o app:
 * um erro de rede nao pode transformar um assinante em usuario bloqueado.
 */
export const PlanProvider = ({ children }) => {
  const { isAuthenticated, loading: carregandoAuth } = useAuth();
  const [plano, setPlano] = useState(PLANO_PADRAO);
  const [carregando, setCarregando] = useState(true);
  const [indisponivel, setIndisponivel] = useState(false);

  const carregar = useCallback(async () => {
    if (!isAuthenticated) {
      setPlano(PLANO_PADRAO);
      setIndisponivel(false);
      setCarregando(false);
      return;
    }

    setCarregando(true);

    try {
      const record = await getMeuPlano();
      setPlano(record ? { ...PLANO_PADRAO, ...record } : PLANO_PADRAO);
      setIndisponivel(false);
    } catch (error) {
      console.error("PlanProvider: nao foi possivel carregar o plano", error);
      setPlano(PLANO_PADRAO);
      setIndisponivel(true);
    } finally {
      setCarregando(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!carregandoAuth) {
      carregar();
    }
  }, [carregandoAuth, carregar]);

  const valor = useMemo(() => {
    const temRecurso = (codigo) => {
      if (indisponivel) return true;
      return plano?.recursos?.[codigo]?.habilitado === true;
    };

    return {
      plano,
      carregando: carregando || carregandoAuth,
      indisponivel,
      temRecurso,
      limiteDoRecurso: (codigo) => plano?.recursos?.[codigo]?.limite ?? null,
      recarregar: carregar,
    };
  }, [plano, carregando, carregandoAuth, indisponivel, carregar]);

  return <PlanContext.Provider value={valor}>{children}</PlanContext.Provider>;
};

export const usePlan = () => {
  const contexto = useContext(PlanContext);

  // Fora do provider (teste, storybook) nada e bloqueado.
  if (!contexto) {
    return {
      plano: PLANO_PADRAO,
      carregando: false,
      indisponivel: true,
      temRecurso: () => true,
      limiteDoRecurso: () => null,
      recarregar: () => {},
    };
  }

  return contexto;
};

export const useFeature = (codigo) => usePlan().temRecurso(codigo);

export default PlanContext;
